import {
  BaseIcon,
  FormWarning,
  TextInput,
  Modal,
} from "@shltelemedicine/shl-ui-lib";
import { useEffect, useState } from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import editIcon from "../../../../assets/shared/edit.svg";
import { emailPattern, numberPattern } from "../../../../constants";
import AlertLevel from "../../../../models/alert";
import { AddEntity, IEntity, State } from "../../../../models/entity.interface";
import { KeyValue } from "../../../../models/key-value";
import { getFormChangedData } from "../../../../utils/formUtil";
import AlertModal, {
  AlertModalProps,
} from "../../../Shared/AlertModal/AlertModal";
import CancelButton from "../../../Shared/CancelButton/CancelButton";
import EntityIcon from "../../../Shared/EntityIcon/EntityIcon";
import SaveButton from "../../../Shared/SaveButton/SaveButton";
import SelectFormInput from "../../../Shared/SelectInput/SelectFormInput";
import TextFormInput from "../../../Shared/TextInput/TextFormInput";
import styles from "./AddEntityModal.module.css";

const states: KeyValue<string>[] = Object.keys(State).map((key) => ({
  key: key,
  value: State[key as keyof typeof State],
}));

type AddEntityModalProps = {
  show?: boolean;
  mode: "add" | "edit";
  entity?: IEntity;
  parentName?: string;
  onClose?: () => void;
  onSave?: (
    selectedEntityId: string,
    entityDetails: AddEntity | Partial<AddEntity>
  ) => Promise<void>;
};

const AddEntityModal = ({
  show,
  mode,
  entity,
  parentName,
  onClose,
  onSave,
}: AddEntityModalProps) => {
  const isEdit = mode === "edit";
  const [saving, setSaving] = useState(false);
  const intl = useIntl();

  const [showEditConfirmModal, setShowEditConfirmModal] = useState(false);

  const {
    reset,
    handleSubmit,
    control,
    formState: { errors: formErrors, isDirty, dirtyFields },
    getValues,
  } = useForm<AddEntity>();

  useEffect(() => {
    if (show === true) {
      reset(
        isEdit
          ? {
              email: entity?.email,
              name: entity?.name,
              phone: entity?.phone,
              reference: entity?.reference,
              state: entity?.state,
            }
          : {}
      );
    }
  }, [show]); // eslint-disable-line

  const saveHandler = async (addEntityForm: AddEntity) => {
    if (isEdit) {
      setShowEditConfirmModal(true);
      return;
    }

    await onSaveConfirmed(addEntityForm);
  };

  const onSaveConfirmed = async (addEntityForm: AddEntity) => {
    if (!onSave || !entity?.id) return;

    const dataToSave = !isEdit
      ? addEntityForm
      : getFormChangedData<AddEntity>(dirtyFields, getValues);

    setSaving(true);
    await onSave(entity.id, {
      ...dataToSave,
    });
    setSaving(false);
  };

  const statePlaceholder = intl.messages[
    "MANAGEMENT.ADD_ENTITY.ADDRESS.STATE_PLACEHOLDER"
  ] as string;

  const nameRequiredMsg = intl.messages[
    "MANAGEMENT.ADD_ENTITY.NAME_REQUIRED"
  ] as string;

  const selectedState = states.find((state) => state.key === entity?.state)
    ?.value;

  return (
    <>
      <Modal
        dialogClassName={styles.dialog}
        centered
        backdrop="static"
        show={show}
        onHide={onClose}
        header={
          <div className="d-flex align-items-center">
            {isEdit ? (
              <BaseIcon icon={editIcon} className={styles.editIcon} />
            ) : (
              <EntityIcon />
            )}

            <span className="mx-1"></span>
            <p className={`${styles.title} mb-0`}>
              <FormattedMessage
                id={isEdit ? "MANAGEMENT.EDIT_ENTITY" : "MANAGEMENT.ADD_ENTITY"}
                defaultMessage={isEdit ? "Edit Entity" : "Add Entity"}
              />
              {!isEdit && parentName && (
                <>
                  <span className="mx-1">
                    <FormattedMessage
                      id="MANAGEMENT.ADD_ENTITY.TO"
                      defaultMessage="to"
                    />
                  </span>

                  <span className="prim-color">{parentName}</span>
                </>
              )}
            </p>
          </div>
        }
        footer={
          <div className="d-flex align-items-center justify-content-end border-top pt-2 px-3 m-0">
            <CancelButton
              onClick={onClose}
              className="mx-2"
              disabled={saving || showEditConfirmModal}
            />

            <SaveButton
              onClick={handleSubmit(saveHandler)}
              className="prim-bg-gradient-color capitalized"
              disabled={saving || showEditConfirmModal || !isDirty}
            >
              <FormattedMessage id="GLOBAL.SAVE" defaultMessage="Save" />
              {saving && (
                <>
                  &nbsp;&nbsp;
                  <Spinner as="span" animation="border" size="sm" />
                </>
              )}
            </SaveButton>
          </div>
        }
      >
        <Form className="py-4 px-3 mb-4">
          <Container fluid className="px-5">
            <Form.Row className="mb-3">
              <Col>
                {/* Name */}
                <Form.Label className="mb-1 app-form-label d-flex align-items-center">
                  <span className="prim-color align-middle">*</span>&nbsp;
                  <span>
                    <FormattedMessage
                      id="MANAGEMENT.ADD_ENTITY.NAME"
                      defaultMessage="Entity Name"
                    />
                  </span>
                  {formErrors?.name && (
                    <>
                      <span className="mx-1"></span>
                      <div className="flex-grow-1">
                        <FormWarning
                          className={`font-sm ${styles.warning}`}
                          message={nameRequiredMsg}
                        />
                      </div>
                    </>
                  )}
                </Form.Label>
                <TextFormInput
                  name={"name"}
                  control={control}
                  rules={{
                    required: true,
                  }}
                  input={{
                    isInvalid: !!formErrors?.name,
                    disabled: saving,
                  }}
                />
              </Col>
            </Form.Row>
            <Form.Row className="mb-3">
              <Col>
                {/* Email */}
                <Form.Label className="mb-1 app-form-label">
                  <span>
                    <FormattedMessage id="USER.EMAIL" defaultMessage="Email" />
                  </span>
                </Form.Label>

                <TextFormInput
                  name={"email"}
                  control={control}
                  rules={{
                    pattern: emailPattern,
                  }}
                  input={{
                    type: "email",
                    isInvalid: !!formErrors?.email,
                    disabled: saving,
                  }}
                />
                {!!formErrors?.email && (
                  <FormWarning
                    className="font-sm flex-grow-1"
                    message={
                      <FormattedMessage
                        id={`${
                          formErrors?.email?.type === "pattern"
                            ? "MANAGEMENT.ADD_USER.EMAIL_PATTERN_REQUIRED"
                            : "MANAGEMENT.ADD_USER.EMAIL_REQUIRED"
                        }`}
                        defaultMessage={`${
                          formErrors?.email?.type === "pattern"
                            ? "Insert Valid Email"
                            : "Insert Email"
                        }`}
                      />
                    }
                  />
                )}
              </Col>
              <Col>
                {/* Phone */}
                <Form.Label className="mb-1 app-form-label">
                  <span>
                    <FormattedMessage id="USER.PHONE" defaultMessage="Phone" />{" "}
                    {"("}
                    <FormattedMessage
                      id="USER.PHONE.DIGITS_ONLY"
                      defaultMessage="Digits Only"
                    />
                    {")"}
                  </span>
                </Form.Label>
                <TextFormInput
                  name={"phone"}
                  control={control}
                  rules={{
                    pattern: numberPattern,
                    minLength: 5,
                  }}
                  input={{
                    isInvalid: !!formErrors?.phone,
                    disabled: saving,
                  }}
                />
                {!!formErrors?.phone && (
                  <FormWarning
                    className="font-sm flex-grow-1 mt-2"
                    message={
                      <FormattedMessage
                        id="MANAGEMENT.ADD_USER.PHONE_PATTERN_REQUIRED"
                        defaultMessage="Insert Digits Only"
                      />
                    }
                  />
                )}
              </Col>
            </Form.Row>
            <Form.Row className="d-flex align-items-end">
              <Col>
                {/* Reference */}
                <Form.Label className="mb-1 app-form-label">
                  <span>
                    <FormattedMessage
                      id="USER.REFERENCE"
                      defaultMessage="Reference"
                    />
                  </span>
                </Form.Label>

                <TextFormInput
                  name={"reference"}
                  control={control}
                  input={{
                    isInvalid: !!formErrors?.reference,
                    disabled: isEdit || saving,
                    readOnly: isEdit || saving,
                  }}
                />
              </Col>
              <div className="mx-1"></div>
              <p className="mb-2 font-sm prim-color">
                <FormattedMessage
                  id="MANAGEMENT.ADD_ENTITY.REFERENCE_HINT"
                  defaultMessage="e.g. number or name"
                />
              </p>
            </Form.Row>
          </Container>
          <div className="border-bottom mx-4 my-3"></div>
          <Container fluid className="px-5">
            <Form.Row>
              <Col>
                {/* State */}
                <Form.Label as="p" className="mb-0 app-form-label">
                  <span>
                    <FormattedMessage
                      id="MANAGEMENT.ADD_ENTITY.ADDRESS"
                      defaultMessage="Address"
                    />
                  </span>
                </Form.Label>
                <Form.Label className="mb-1 app-form-label d-flex align-items-center">
                  {!isEdit && (
                    <>
                      <span className="prim-color align-middle">*</span>
                      &nbsp;
                    </>
                  )}
                  <span>
                    <FormattedMessage
                      id="MANAGEMENT.ADD_ENTITY.ADDRESS.STATE"
                      defaultMessage="State"
                    />
                  </span>
                  {!isEdit && formErrors?.state && (
                    <>
                      <span className="mx-1"></span>
                      <div className="flex-grow-1">
                        <FormWarning
                          className={`font-sm ${styles.warning}`}
                          message={statePlaceholder}
                        />
                      </div>
                    </>
                  )}
                </Form.Label>

                {!isEdit ? (
                  <SelectFormInput<AddEntity>
                    name={"state"}
                    control={control}
                    rules={{
                      required: true,
                    }}
                    input={{
                      items: states,
                      placeholder: statePlaceholder,
                      disabled: saving,
                      isInvalid: !!formErrors?.state,
                    }}
                  />
                ) : (
                  <TextInput disabled readOnly value={selectedState} />
                )}
              </Col>
            </Form.Row>
          </Container>
        </Form>
      </Modal>
      <EditConfirmationModal
        dialog
        show={showEditConfirmModal}
        okBtn={{
          onClick: () => {
            setShowEditConfirmModal(false);
            onSaveConfirmed(getValues());
          },
          text: <FormattedMessage id="GLOBAL.YES" defaultMessage="YES" />,
        }}
        onClose={() => setShowEditConfirmModal(false)}
        level={AlertLevel.Warning}
        support={{ showLink: false }}
      />
    </>
  );
};

export default AddEntityModal;

const EditConfirmationModal = (props: AlertModalProps) => {
  return (
    <AlertModal {...props}>
      <FormattedMessage
        id="MANAGEMENT.EDIT_ENTITY.CONFIRMATION"
        defaultMessage="Are you sure you want to update the properties of this Entity?"
      />
    </AlertModal>
  );
};
